/**
 * PostHog facade — lazy SDK behind a PERMANENT delegating bridge on window.posthog.
 *
 * To fire an event use the track() helper:
 *   import { track } from '../shared/analytics/track'
 *   track('app_opened', { device: 'mobile' })
 *
 * Direct `import posthog from 'posthog-js'` is forbidden outside this file
 * (eslint no-restricted-imports): the SDK (~58 kB gz) must never enter the boot graph.
 *
 * WHY a permanent facade: consumers (e.g. AnalyticsRepository) freeze the
 * window.posthog reference at construction time. Replacing the object after the
 * SDK loads would leave those references pointing at a dead stub — so the facade
 * stays forever and delegates internally once the real SDK arrives.
 */
import { Logger } from '@theidiomaclub/logging'

type QueuedCall =
    | { readonly method: 'capture'; readonly args: readonly [string, Record<string, unknown>?] }
    | { readonly method: 'identify'; readonly args: readonly [string, Record<string, unknown>?] }
    | { readonly method: 'reset'; readonly args: readonly [] }

interface PostHogSdk {
    init(key: string, options: Record<string, unknown>): void
    capture(event: string, properties?: Record<string, unknown>): void
    identify(id: string, properties?: Record<string, unknown>): void
    reset(): void
    opt_out_capturing(): void
}

export interface PostHogFacade {
    capture(event: string, properties?: Record<string, unknown>): void
    identify(id: string, properties?: Record<string, unknown>): void
    reset(): void
}

// Read at call time (not module scope) so tests can stub the env per case.
const getKey = (): string | undefined => {
    const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
    return key && key.trim() !== '' ? key : undefined
}
const getHost = (): string =>
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://app.posthog.com'

let realPosthog: PostHogSdk | null = null
let sdkLoadPromise: Promise<void> | null = null
const queue: QueuedCall[] = []

const facade: PostHogFacade = {
    capture(event, properties) {
        if (!getKey()) return
        if (realPosthog) realPosthog.capture(event, properties)
        else queue.push({ method: 'capture', args: [event, properties] })
    },
    identify(id, properties) {
        if (!getKey()) return
        if (realPosthog) realPosthog.identify(id, properties)
        else queue.push({ method: 'identify', args: [id, properties] })
    },
    reset() {
        if (!getKey()) return
        if (realPosthog) realPosthog.reset()
        else queue.push({ method: 'reset', args: [] })
    },
}

function flushQueue(sdk: PostHogSdk): void {
    // Single FIFO replayed in original call order — identifies land before later captures.
    for (const call of queue.splice(0, queue.length)) {
        switch (call.method) {
            case 'capture': sdk.capture(...call.args); break
            case 'identify': sdk.identify(...call.args); break
            case 'reset': sdk.reset(); break
        }
    }
}

/**
 * Loads posthog-js immediately — conversion events must not sit in the
 * in-memory queue. Called by the idle scheduler on first real interaction.
 */
export function loadPostHogNow(): Promise<void> {
    const key = getKey()
    if (!key) return Promise.resolve()
    if (sdkLoadPromise) return sdkLoadPromise

    sdkLoadPromise = import('posthog-js')
        .then((mod) => {
            const sdk = mod.default as unknown as PostHogSdk
            sdk.init(key, {
                api_host:          getHost(),
                person_profiles:   'identified_only', // solo crea perfil cuando se llama identify()
                capture_pageview:  false,             // SPA — manejamos page views manualmente
                capture_pageleave: true,
                autocapture:       false,             // solo eventos manuales — sin ruido
                loaded: (ph: PostHogSdk) => {
                    if (import.meta.env.DEV) ph.opt_out_capturing()
                },
            })
            realPosthog = sdk
            flushQueue(sdk)
        })
        .catch((err: unknown) => {
            // Fail loud in logs, never in UX: analytics must not crash the app,
            // but a dead SDK load (adblocker, 3G) must be visible to us.
            Logger.warn('posthog-js SDK failed to load — queued events dropped', {
                action: 'posthog_sdk_load_failed',
                error: err instanceof Error ? err.message : String(err),
            })
        })

    return sdkLoadPromise
}

/**
 * Schedules the SDK load behind the FIRST user interaction, with a short idle
 * fallback. Call AFTER React mounts.
 *
 * WHY interaction-gated (not bare requestIdleCallback): once the boot-critical work
 * finishes, the main thread goes idle and requestIdleCallback would fire mid-load,
 * pulling the ~58 kB SDK into the first-load window (hurting LCP/TBT on 3G Android).
 * Gating on the first tap keeps the SDK out of the critical path for engaged users;
 * the fallback timer still fires it for sessions that never interact, so bounce
 * analytics (app_opened queued in the facade) are preserved and replayed on load.
 */
export function schedulePostHogIdleLoad(): void {
    if (!getKey()) return
    let fired = false
    // Only genuine user-intent events — NOT scroll/visibilitychange, which Lighthouse
    // and the browser can synthesize (would pull the SDK into the audit window).
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart']
    const trigger = () => {
        if (fired) return
        fired = true
        events.forEach((e) => window.removeEventListener(e, trigger))
        void loadPostHogNow()
    }
    events.forEach((e) => window.addEventListener(e, trigger, { passive: true }))
    // Fallback: idle sessions that never interact still get analytics (queued events flush).
    setTimeout(trigger, 6000)
}

/**
 * Installs the permanent facade on window.posthog (ALWAYS, even without a key —
 * consumers must never find it undefined). Does NOT load the SDK — the load is
 * triggered post-mount via schedulePostHogIdleLoad() / loadPostHogNow().
 */
export function initPostHog(): void {
    if ((window as unknown as { posthog?: PostHogFacade }).posthog !== facade) {
        ;(window as unknown as { posthog: PostHogFacade }).posthog = facade
    }
}

export function identifyUser(userId: string, traits: {
    role:       string
    status:     string
    tenant_id?: string | null
}): void {
    facade.identify(userId, {
        role:      traits.role,
        status:    traits.status,
        tenant_id: traits.tenant_id ?? null,
    })
}

export function resetPostHog(): void {
    // NEVER delete window.posthog — frozen references (AnalyticsRepository) must
    // keep working for the next session after logout→login.
    facade.reset()
}
